import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useConfig } from '@config/provider';
import { DEFAULT_POST_LOGIN_REDIRECT_PATH, decodeSsoState, ensureSafeRedirectPath } from '@app/utils/sso';

type ScenarioKey = 'oidc' | 'saml' | 'saml-idp';

interface ScenarioConfig {
  heading: string;
  description: string;
  txParam: 'oidcTx' | 'samlTx';
  txPrefix: string;
  defaultConnection: string;
}

const SCENARIOS: Record<ScenarioKey, ScenarioConfig> = {
  oidc: {
    heading: 'Mock OIDC provider',
    description: 'Issue a mock OIDC transaction handle back to REAP.',
    txParam: 'oidcTx',
    txPrefix: 'mock-oidc',
    defaultConnection: 'Mock OIDC Connection'
  },
  saml: {
    heading: 'Mock SAML (SP-initiated)',
    description: 'Return a mock SAML transaction handle for the service-provider initiated flow.',
    txParam: 'samlTx',
    txPrefix: 'mock-saml',
    defaultConnection: 'Mock SAML SP Connection'
  },
  'saml-idp': {
    heading: 'Mock SAML (IdP-initiated)',
    description: 'Simulate an IdP portal posting back to REAP after authentication.',
    txParam: 'samlTx',
    txPrefix: 'mock-saml-idp',
    defaultConnection: 'Mock SAML IdP Connection'
  }
};

const MODE_ALIASES: Record<string, ScenarioKey> = {
  oidc: 'oidc',
  saml: 'saml',
  'saml-sp': 'saml',
  'saml-idp': 'saml-idp',
  'idp-saml': 'saml-idp',
  idp: 'saml-idp'
};

const createHandleSuffix = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
};

const MockSsoPage = () => {
  const { mockRoutes } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode: rawMode } = useParams<{ mode?: string }>();
  const [searchParams] = useSearchParams();

  const mocksEnabled = useMemo(() => mockRoutes.includes('auth'), [mockRoutes]);

  const scenarioKey = useMemo<ScenarioKey | null>(() => {
    if (location.pathname.startsWith('/mock/idp')) {
      return 'saml-idp';
    }
    const normalized = (rawMode ?? '').toLowerCase();
    return MODE_ALIASES[normalized] ?? null;
  }, [location.pathname, rawMode]);

  const scenario = scenarioKey ? SCENARIOS[scenarioKey] : null;

  const stateToken = useMemo(
    () => searchParams.get('state') ?? searchParams.get('relayState') ?? searchParams.get('RelayState'),
    [searchParams]
  );
  const redirectParam = searchParams.get('redirect');
  const decodedState = useMemo(() => (stateToken ? decodeSsoState(stateToken) : null), [stateToken]);
  const redirectPath = useMemo(
    () => ensureSafeRedirectPath(decodedState?.redirectPath ?? redirectParam ?? DEFAULT_POST_LOGIN_REDIRECT_PATH),
    [decodedState?.redirectPath, redirectParam]
  );

  const orgId = searchParams.get('orgId');
  const connection = searchParams.get('connection');
  const loginHint = searchParams.get('login_hint');

  const completeSuccess = useCallback(() => {
    if (!scenario) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams();
    params.set(scenario.txParam, `${scenario.txPrefix}-${createHandleSuffix()}`);
    if (stateToken) {
      params.set('state', stateToken);
    }
    if (redirectParam) {
      params.set('redirect', redirectParam);
    }
    if (orgId) {
      params.set('orgId', orgId);
    }
    if (loginHint) {
      params.set('login_hint', loginHint);
    }

    window.location.assign(`/sso/callback?${params.toString()}`);
  }, [loginHint, orgId, redirectParam, scenario, stateToken]);

  const completeError = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams();
    params.set('error', 'mock_sso_failure');
    if (stateToken) {
      params.set('state', stateToken);
    }
    if (redirectParam) {
      params.set('redirect', redirectParam);
    }
    window.location.assign(`/sso/callback?${params.toString()}`);
  }, [redirectParam, stateToken]);

  useEffect(() => {
    const auto = (searchParams.get('auto') ?? '').toLowerCase();
    if (auto === 'success') {
      completeSuccess();
    } else if (auto === 'error') {
      completeError();
    }
  }, [completeError, completeSuccess, searchParams]);

  if (!mocksEnabled) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Mock routes disabled</h1>
            <p className="auth-subtitle">Enable the auth mock routes (UI_MOCK_ROUTES includes “auth”) to use this page.</p>
          </header>
          <div className="auth-inline-panel">
            <p>
              Run <code>make docker-run</code> locally or set <code>UI_MOCK_ROUTES=auth</code> to simulate SSO flows.
            </p>
          </div>
          <div>
            <button type="button" className="auth-submit" onClick={() => navigate('/login')}>
              Back to sign-in
            </button>
          </div>
        </section>
      </main>
    );
  }

  if (!scenario) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Unknown mock scenario</h1>
            <p className="auth-subtitle">The requested mock path does not match a known SSO flow.</p>
          </header>
          <div className="auth-inline-panel">
            <p>
              Supported paths:
              <br />
              <code>/mock/sso/oidc</code>, <code>/mock/sso/saml</code>, <code>/mock/sso/saml-idp</code>
            </p>
          </div>
          <div>
            <button type="button" className="auth-submit" onClick={() => navigate('/login')}>
              Back to sign-in
            </button>
          </div>
        </section>
      </main>
    );
  }

  const connectionName = connection ?? scenario.defaultConnection;

  return (
    <main className="auth-layout">
      <section className="auth-card">
        <header className="auth-card__header">
          <h1 className="auth-title">{scenario.heading}</h1>
          <p className="auth-subtitle">{scenario.description}</p>
        </header>

        <section className="auth-inline-panel">
          <h2 className="auth-inline-panel__title">Request details</h2>
          <dl>
            <div>
              <dt>Connection</dt>
              <dd>{connectionName}</dd>
            </div>
            <div>
              <dt>Org ID</dt>
              <dd>{orgId ?? '—'}</dd>
            </div>
            <div>
              <dt>Login hint</dt>
              <dd>{loginHint ?? '—'}</dd>
            </div>
            <div>
              <dt>Relay target</dt>
              <dd>
                <code>{redirectPath}</code>
              </dd>
            </div>
          </dl>
        </section>

        <div>
          <button type="button" className="auth-submit" onClick={completeSuccess}>
            Issue {scenario.txParam}
          </button>
          <button type="button" className="auth-ghost" onClick={completeError}>
            Return error
          </button>
          <button type="button" className="auth-link" onClick={() => navigate('/login')}>
            Cancel
          </button>
        </div>
      </section>
    </main>
  );
};

export default MockSsoPage;
